import React from 'react';
import {
  Banner,
  BlockStack,
  Box,
  Button,
  InlineStack,
  LegacyCard as Card,
  Text,
  TextField
} from '@shopify/polaris';
import { buildSummaryGroups } from '../../../utils/storefrontOrder/payload';

/**
 * Order summary card: per-story pricing from the quote, shipping, every
 * discount row (including automatic ones), the total, the discount code
 * box, and the Place order action.
 *
 * @param {Object} props
 * @param {Object[]} props.entries - Entry list.
 * @param {Object|null} props.quote - Quote from calculate-shopify-cart.
 * @param {string} props.status - useOrderQuote status.
 * @param {string|null} props.error - Quote error message.
 * @param {string} props.discountInput - Raw discount code input.
 * @param {Function} props.onDiscountChange - Receives the next input value.
 * @param {Function} props.onApplyDiscount - Applies the current input.
 * @param {Function} [props.onRemoveDiscount] - Receives the applied code to remove.
 * @param {boolean} props.canSubmit - Form valid and quote ready.
 * @param {boolean} props.submitting - A submit is in flight.
 * @param {string|null} props.submitError - Submit failure banner text.
 * @param {Function} props.onSubmit
 * @param {Function} props.onRetryQuote - Re-fetch the quote bypassing cache.
 * @param {boolean} [props.disabled]
 * @returns {JSX.Element}
 */
export default function OrderSummaryCard({
  entries,
  quote,
  status,
  error,
  discountInput,
  onDiscountChange,
  onApplyDiscount,
  onRemoveDiscount,
  canSubmit,
  submitting,
  submitError,
  onSubmit,
  onRetryQuote,
  disabled = false
}) {
  const lineItems = quote && Array.isArray(quote.line_items) ? quote.line_items : [];
  const discounts = quote && Array.isArray(quote.discounts) ? quote.discounts : [];

  // Per-story groups: the story line paired by index, plus that story's
  // ticked add-on lines. Add-ons are already priced inside quote.total, so
  // every row is display-only.
  const groups = buildSummaryGroups(entries, lineItems);

  const discountMessage = (() => {
    if (!quote || !quote.discount_code) {
      return null;
    }
    if (quote.discount_code.valid === false) {
      return 'Invalid discount code';
    }
    if (quote.discount_code.valid && quote.discount_code.applied === false) {
      return 'Valid but does not apply to the selected items';
    }
    return null;
  })();

  // The code the user applied (not automatic discounts) can be removed.
  const appliedCode =
    quote && quote.discount_code && quote.discount_code.valid && quote.discount_code.applied
      ? String(quote.discount_code.code || '').trim()
      : '';

  return (
    <Card>
      <Card.Section title="Order summary">
        <BlockStack gap="300">
          {status === 'loading' ? (
            <Text as="p" tone="subdued">
              Calculating pricing…
            </Text>
          ) : null}

          {status === 'error' && error ? (
            <Banner tone="critical" action={{ content: 'Retry', onAction: onRetryQuote }}>
              <Text as="p">{error}</Text>
            </Banner>
          ) : null}

          {quote ? (
            <BlockStack gap="300">
              {groups.map(group => {
                if (!group.storyLine) {
                  return null;
                }
                const { entry, storyLine, addons } = group;
                return (
                  <BlockStack key={group.key} gap="100">
                    <InlineStack align="space-between" blockAlign="start">
                      <BlockStack gap="050">
                        <Text as="span" fontWeight="semibold">
                          {entry.story.title}
                          {entry.premiumPrinting && entry.variant && entry.variant.premium
                            ? ' · Premium printing'
                            : ''}
                        </Text>
                        <Text as="span" tone="subdued">
                          {[entry.variant.title, entry.kidName].filter(Boolean).join(' · ')}
                        </Text>
                      </BlockStack>
                      <Text as="span">{storyLine.original_total} EGP</Text>
                    </InlineStack>
                    {addons.map(addon => (
                      <Box key={`addon-${group.key}-${addon.sku}`} paddingInlineStart="600">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="span" tone="subdued">
                            {addon.label}
                            {addon.quantity && addon.quantity > 1 ? ` ×${addon.quantity}` : ''}
                          </Text>
                          <Text as="span">{addon.originalTotal} EGP</Text>
                        </InlineStack>
                      </Box>
                    ))}
                  </BlockStack>
                );
              })}

              {discounts.map((discount, index) => {
                const code = String(discount.title || discount.code || '').trim();
                const removable = appliedCode && code === appliedCode && onRemoveDiscount;
                return (
                  <InlineStack
                    key={`discount-${index}`}
                    align="space-between"
                    blockAlign="center"
                  >
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span" tone="success">
                        Discount: {discount.title || discount.code || 'Discount'}
                      </Text>
                      {removable ? (
                        <Button
                          plain
                          monochrome
                          onClick={() => onRemoveDiscount(code)}
                          disabled={disabled}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </InlineStack>
                    <Text as="span" tone="success">
                      -{discount.amount} EGP
                    </Text>
                  </InlineStack>
                );
              })}

              {quote.shipping_total ? (
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="span">Delivery</Text>
                  <Text as="span">{quote.shipping_total} EGP</Text>
                </InlineStack>
              ) : null}

              <InlineStack align="space-between" blockAlign="center">
                <Text as="span" fontWeight="bold">
                  Total
                </Text>
                <Text as="span" fontWeight="bold">
                  {quote.total} EGP
                </Text>
              </InlineStack>
            </BlockStack>
          ) : null}

          {discountMessage ? (
            <Text as="p" tone="critical">
              {discountMessage}
            </Text>
          ) : null}

          <InlineStack gap="300" blockAlign="end">
            <TextField
              label="Discount code"
              value={discountInput}
              onChange={onDiscountChange}
              disabled={disabled}
              autoComplete="off"
            />
            <Button
              onClick={onApplyDiscount}
              disabled={disabled || !discountInput.trim()}
            >
              Apply
            </Button>
          </InlineStack>

          {submitError ? (
            <Banner tone="critical">
              <Text as="p">{submitError}</Text>
            </Banner>
          ) : null}

          <Button
            variant="primary"
            size="large"
            disabled={!canSubmit || disabled}
            loading={submitting}
            onClick={onSubmit}
          >
            Place order
          </Button>
        </BlockStack>
      </Card.Section>
    </Card>
  );
}
